import Cookies from 'js-cookie';
import { useCallback, useEffect, useRef } from 'react';
import { useAuth } from '../global-components/src';
import {
    clearPapoWebSession,
    PAPO_TOKEN_COOKIE,
    PAPO_USER_COOKIE,
    synchronizePapoWebSession,
} from '../utils/papo-web-session';

const POLL_INTERVAL_MS = 5_000;

type PapoSessionUser = {
    id?: number;
};

const readPapoUser = (): PapoSessionUser | null => {
    const storedUser = Cookies.get(PAPO_USER_COOKIE);
    if (!storedUser) return null;

    try {
        return JSON.parse(storedUser) as PapoSessionUser;
    } catch {
        return null;
    }
};

const isRemembered = (userId: number) => {
    try {
        const savedUsers = JSON.parse(Cookies.get('saved_users') ?? '[]') as Array<{ id?: number }>;
        return savedUsers.some(savedUser => savedUser.id === userId);
    } catch {
        return false;
    }
};

/**
 * Keeps the browser session mirrored in the independent Paçoca Papo web
 * scope. Cookie polling is necessary because the two frontends use distinct
 * subdomains and therefore cannot receive each other's storage events.
 */
export default function PapoWebSessionSynchronizer() {
    const { isAuthenticated, token, user } = useAuth();
    const synchronizationInFlight = useRef(false);

    const synchronize = useCallback(() => {
        const socialCookieToken = Cookies.get('token') ?? '';
        if (socialCookieToken !== token) {
            window.location.reload();
            return;
        }

        if (!isAuthenticated || !token || !user) {
            clearPapoWebSession();
            return;
        }

        const papoToken = Cookies.get(PAPO_TOKEN_COOKIE);
        const papoUser = readPapoUser();
        if (papoToken && papoUser?.id === user.id) return;
        if (synchronizationInFlight.current) return;

        synchronizationInFlight.current = true;
        void synchronizePapoWebSession(token, user, isRemembered(user.id))
            .catch(() => undefined)
            .finally(() => {
                synchronizationInFlight.current = false;
            });
    }, [isAuthenticated, token, user]);

    useEffect(() => {
        synchronize();

        const synchronizeWhenVisible = () => {
            if (document.visibilityState === 'visible') synchronize();
        };
        const interval = window.setInterval(synchronize, POLL_INTERVAL_MS);
        window.addEventListener('focus', synchronize);
        document.addEventListener('visibilitychange', synchronizeWhenVisible);

        return () => {
            window.clearInterval(interval);
            window.removeEventListener('focus', synchronize);
            document.removeEventListener('visibilitychange', synchronizeWhenVisible);
        };
    }, [synchronize]);

    return null;
}
